import React, { useState, useEffect } from 'react';
import '../App.css';
import { fetchPokedex, fetchAllTypes } from '../components/FetchPokemon';
import TypeGrid from '../components/TypeGrid';
import DexSelector from '../components/DexSelector';
import SearchBar from '../components/SearchBar';
import PokemonGrid from '../components/PokemonGrid';

const API_BASE = process.env.REACT_APP_POKEMON_API_URL;

// Pokémon type colours for squad headers
const TYPE_COLOURS = {
  normal:   '#A8A878', fire:     '#F08030', water:    '#6890F0',
  electric: '#F8D030', grass:    '#78C850', ice:      '#98D8D8',
  fighting: '#C03028', poison:   '#A040A0', ground:   '#E0C068',
  flying:   '#A890F0', psychic:  '#F85888', bug:      '#A8B820',
  rock:     '#B8A038', ghost:    '#705898', dragon:   '#7038F8',
  dark:     '#705848', steel:    '#B8B8D0', fairy:    '#EE99AC',
};


// ─────────────────────────────────────────────────────────────────────────────
// TeamSlot
// ─────────────────────────────────────────────────────────────────────────────
function TeamSlot({ pokemon, onRemove }) {
  return (
    <div style={styles.filledSlot} title={pokemon.name}>
      <img src={pokemon.sprite} alt={pokemon.name} style={styles.slotSprite} />
      <span style={styles.slotName}>{pokemon.name}</span>
      <button onClick={() => onRemove(pokemon.id)} style={styles.removeBtn} title="Remove">×</button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// TeamPanel  — one team's expanded view
// ─────────────────────────────────────────────────────────────────────────────
function TeamPanel({ team, activeTeamId, onSetActiveTeam, onRemove, onClear, onRename, onDelete, typeColour, collapsed, onToggle }) {
  const [editing, setEditing] = useState(false);
  const [draftName, setDraftName] = useState(team.name);
  const isActive = team.id === activeTeamId;

  const commitRename = () => {
    const trimmed = draftName.trim();
    if (trimmed) onRename(team.id, trimmed);
    setEditing(false);
  };

  return (
    <div style={{ ...styles.teamPanel, borderColor: isActive ? typeColour : 'rgba(255,255,255,0.1)' }}>
      {/* Team row header */}
      <div style={{ ...styles.teamPanelHeader, cursor: 'pointer', marginBottom: collapsed ? 0 : '0.4rem' }} onClick={onToggle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flex: 1 }}>
          {editing ? (
            <input
              autoFocus
              value={draftName}
              onChange={e => setDraftName(e.target.value)}
              onBlur={commitRename}
              onKeyDown={e => { if (e.key === 'Enter') commitRename(); if (e.key === 'Escape') setEditing(false); }}
              style={styles.nameInput}
              onClick={e => e.stopPropagation()}
            />
          ) : (
            <span
              style={{ ...styles.teamPanelName, color: isActive ? typeColour : '#ccc' }}
              onClick={e => { e.stopPropagation(); setEditing(true); }}
              title="Click to rename"
            >
              {team.name} <span style={styles.editHint}>✏</span>
            </span>
          )}
          <span style={styles.countBadge}>{team.members.length} Pokémon</span>
          <span style={{ ...styles.collapseIcon, color: typeColour }}>{collapsed ? '▶' : '▼'}</span>
        </div>

        <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }} onClick={e => e.stopPropagation()}>
          <button
            onClick={() => onSetActiveTeam(isActive ? null : team.id)}
            style={{
              ...styles.selectBtn,
              background: isActive ? typeColour : 'transparent',
              borderColor: typeColour,
              color: isActive ? '#111' : typeColour,
            }}
          >
            {isActive ? '✓ Selected' : 'Select'}
          </button>
          <button onClick={() => onClear(team.id)} style={styles.clearBtn}>Clear</button>
          <button onClick={() => onDelete(team.id)} style={styles.deleteBtn} title="Delete team">🗑</button>
        </div>
      </div>

      {/* Members */}
      {!collapsed && team.members.length > 0 && (
        <div style={styles.slotsGrid}>
          {team.members.map(poke => (
            <TeamSlot key={poke.id} pokemon={poke} onRemove={id => onRemove(team.id, id)} />
          ))}
        </div>
      )}

      {!collapsed && team.members.length === 0 && (
        <p style={{ ...styles.emptyMsg, padding: '0.3rem 0' }}>No Pokémon yet — select this team and use <strong>+ Add</strong> on the grid.</p>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// SquadSection  — collapsible section for one squad
// ─────────────────────────────────────────────────────────────────────────────
function SquadSection({ squad, activeTeamId, onSetActiveTeam, onRemoveMember, onClearTeam, onRenameTeam, onDeleteTeam, onAddTeamToSquad, onDeleteSquad, collapsed, onToggleSquad, collapsedTeams, onToggleTeam, onExpandAllTeams, onCollapseAllTeams }) {
  const colour = TYPE_COLOURS[squad.type] || '#e8b84b';
  const totalMembers = squad.teams.reduce((n, t) => n + t.members.length, 0);

  return (
    <div style={{ ...styles.squadSection, borderColor: colour }}>
      {/* Squad header */}
      <div style={styles.squadHeader} onClick={() => onToggleSquad(squad.id)}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
          <span style={{ ...styles.typeChip, background: colour }}>
            {(squad.name || squad.type || '').charAt(0).toUpperCase() + (squad.name || squad.type || '').slice(1)}
          </span>
          <span style={styles.squadMeta}>{squad.teams.length} teams · {totalMembers} Pokémon</span>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }} onClick={e => e.stopPropagation()}>
          {!collapsed && (
            <>
              <button onClick={() => onExpandAllTeams(squad.id)} style={styles.expandCollapseBtn}>Expand all</button>
              <button onClick={() => onCollapseAllTeams(squad.id)} style={styles.expandCollapseBtn}>Collapse all</button>
            </>
          )}
          <button onClick={() => onAddTeamToSquad(squad.id)} style={{ ...styles.addTeamBtn, borderColor: colour, color: colour }}>
            + Add Team
          </button>
          <button onClick={() => onDeleteSquad(squad.id)} style={styles.deleteBtn} title="Delete squad">🗑</button>
          <span style={{ ...styles.collapseIcon, color: colour }}>{collapsed ? '▶' : '▼'}</span>
        </div>
      </div>

      {/* Teams */}
      {!collapsed && (
        <div style={styles.teamsContainer}>
          {squad.teams.length === 0 ? (
            <p style={styles.emptyMsg}>No teams yet — click <strong>+ Add Team</strong> above.</p>
          ) : (
            squad.teams.map(team => (
              <TeamPanel
                key={team.id}
                team={team}
                activeTeamId={activeTeamId}
                onSetActiveTeam={onSetActiveTeam}
                onRemove={onRemoveMember}
                onClear={onClearTeam}
                onRename={onRenameTeam}
                onDelete={onDeleteTeam}
                typeColour={colour}
                collapsed={collapsedTeams.has(team.id)}
                onToggle={() => onToggleTeam(team.id)}
              />
            ))
          )}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// SquadsManager  — the full panel
// ─────────────────────────────────────────────────────────────────────────────
function SquadsManager({ squads, unassignedTeams, isOpen, onClose, triggerSquadInput, newSquadId, activeTeamId, onSetActiveTeam, onCreateSquad, onDeleteSquad, onAddTeamToSquad, onAssignToSquad, onDeleteTeam, onRemoveMember, onClearTeam, onRenameTeam, onExport, onImport }) {
  const [showSquadInput, setShowSquadInput] = useState(false);
  const [newSquadName, setNewSquadName] = useState('');
  const [collapsedSquads, setCollapsedSquads] = useState(() => new Set(squads.map(s => s.id)));
  const [collapsedTeams, setCollapsedTeams] = useState(() => new Set([
    ...squads.flatMap(s => s.teams.map(t => t.id)),
    ...unassignedTeams.map(t => t.id),
  ]));
  const [unassignedCollapsed, setUnassignedCollapsed] = useState(false);

  // Keep a ref to latest squads so effects always see current data
  const squadsRef = React.useRef(squads);
  React.useEffect(() => { squadsRef.current = squads; }, [squads]);

  // Open the squad name input whenever the external button is clicked
  React.useEffect(() => {
    if (triggerSquadInput > 0) setShowSquadInput(true);
  }, [triggerSquadInput]);

  // Auto-expand a newly created squad
  React.useEffect(() => {
    if (!newSquadId) return;
    setCollapsedSquads(prev => {
      const next = new Set(prev);
      next.delete(newSquadId);
      return next;
    });
  }, [newSquadId]);

  // Auto-expand active team + its parent squad whenever activeTeamId changes
  React.useEffect(() => {
    if (!activeTeamId) return;
    // Defer by one tick so squads state has settled after addTeamToSquad
    const timer = setTimeout(() => {
      // Expand the team itself
      setCollapsedTeams(prev => {
        const next = new Set(prev);
        next.delete(activeTeamId);
        return next;
      });
      // Expand the parent squad using the ref so we always have fresh data
      const parentSquad = squadsRef.current.find(s => s.teams.some(t => t.id === activeTeamId));
      if (parentSquad) {
        setCollapsedSquads(prev => {
          const next = new Set(prev);
          next.delete(parentSquad.id);
          return next;
        });
      }
    }, 0);
    return () => clearTimeout(timer);
  }, [activeTeamId]);
  const allTeamIds = squads.flatMap(s => s.teams.map(t => t.id));

  const toggleSquad = id => setCollapsedSquads(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const toggleTeam = id => setCollapsedTeams(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const expandAllTeams = setId => {
    const teamIds = squads.find(s => s.id === setId)?.teams.map(t => t.id) || [];
    setCollapsedTeams(prev => { const next = new Set(prev); teamIds.forEach(id => next.delete(id)); return next; });
  };

  const collapseAllTeams = setId => {
    const teamIds = squads.find(s => s.id === setId)?.teams.map(t => t.id) || [];
    setCollapsedTeams(prev => { const next = new Set(prev); teamIds.forEach(id => next.add(id)); return next; });
  };

  const expandEverything = () => {
    setCollapsedSquads(new Set());
    setCollapsedTeams(new Set());
  };

  const collapseEverything = () => {
    setCollapsedSquads(new Set(squads.map(s => s.id)));
    setCollapsedTeams(new Set(allTeamIds));
  };

  // Collapse newly added teams by default
  const allTeamIdsKey = allTeamIds.join(',');
  const prevTeamIdsRef = React.useRef(new Set(allTeamIds));
  React.useEffect(() => {
    const prev = prevTeamIdsRef.current;
    allTeamIds.forEach(id => {
      if (!prev.has(id)) {
        setCollapsedTeams(ct => { const next = new Set(ct); next.add(id); return next; });
      }
    });
    prevTeamIdsRef.current = new Set(allTeamIds);
  }, [allTeamIdsKey]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!isOpen) return null;

  return (
    <div style={styles.managerOverlay}>
      <div style={styles.managerBox}>
        {/* Top bar */}
        <div style={styles.managerTopBar}>
          <span style={styles.managerTitle}>⚔ Team Squads</span>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            {squads.length > 0 && (
              <>
                <button onClick={expandEverything} style={styles.expandCollapseBtn}>Expand all</button>
                <button onClick={collapseEverything} style={styles.expandCollapseBtn}>Collapse all</button>
              </>
            )}
            <button onClick={onExport} style={styles.expandCollapseBtn} title="Export teams to JSON">⬇ Export</button>
            <label style={{ ...styles.expandCollapseBtn, cursor: 'pointer' }} title="Import teams from JSON">
              ⬆ Import
              <input
                type="file"
                accept=".json"
                style={{ display: 'none' }}
                onChange={e => { onImport(e.target.files[0]); e.target.value = ''; }}
              />
            </label>
            <button onClick={() => setShowSquadInput(p => !p)} style={styles.newSquadBtn}>
              {showSquadInput ? 'Cancel' : '+ New Squad'}
            </button>
            <button onClick={onClose} style={styles.closeBtn}>✕</button>
          </div>
        </div>

        {/* Squad name input */}
        {showSquadInput && (
          <div style={styles.squadInput}>
            <p style={styles.squadInputLabel}>Type your Squad's name:</p>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', justifyContent: 'center' }}>
              <input
                autoFocus
                type="text"
                value={newSquadName}
                onChange={e => setNewSquadName(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter' && newSquadName.trim()) {
                    onCreateSquad(newSquadName.trim());
                    setNewSquadName('');
                    setShowSquadInput(false);
                  }
                  if (e.key === 'Escape') setShowSquadInput(false);
                }}
                placeholder="e.g. Dragon Squad, Kanto Run..."
                style={styles.squadNameInput}
              />
              <button
                disabled={!newSquadName.trim()}
                onClick={() => {
                  if (!newSquadName.trim()) return;
                  onCreateSquad(newSquadName.trim());
                  setNewSquadName('');
                  setShowSquadInput(false);
                }}
                style={{
                  ...styles.newSquadBtn,
                  opacity: newSquadName.trim() ? 1 : 0.4,
                  cursor: newSquadName.trim() ? 'pointer' : 'default',
                }}
              >
                Create
              </button>
            </div>
          </div>
        )}

        {/* Unassigned teams */}
        {unassignedTeams.length > 0 && (
          <div style={{ ...styles.squadSection, borderColor: 'rgba(255,255,255,0.2)', marginBottom: '0.6rem' }}>
            <div style={styles.squadHeader} onClick={() => setUnassignedCollapsed(c => !c)}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                <span style={{ ...styles.typeChip, background: 'rgba(255,255,255,0.15)', color: '#ccc' }}>
                  Unassigned
                </span>
                <span style={styles.squadMeta}>{unassignedTeams.length} teams</span>
              </div>
              <span style={{ ...styles.collapseIcon, color: '#888' }}>{unassignedCollapsed ? '▶' : '▼'}</span>
            </div>
            {!unassignedCollapsed && (
              <div style={styles.teamsContainer}>
                {unassignedTeams.map(team => (
                  <div key={team.id}>
                    <TeamPanel
                      team={team}
                      activeTeamId={activeTeamId}
                      onSetActiveTeam={onSetActiveTeam}
                      onRemove={onRemoveMember}
                      onClear={onClearTeam}
                      onRename={onRenameTeam}
                      onDelete={onDeleteTeam}
                      typeColour='#888'
                      collapsed={collapsedTeams.has(team.id)}
                      onToggle={() => toggleTeam(team.id)}
                    />
                    {/* Assign to squad dropdown */}
                    {squads.length > 0 && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginTop: '0.25rem', paddingLeft: '0.25rem' }}>
                        <span style={{ fontSize: '0.72rem', color: '#666' }}>Assign to:</span>
                        <select
                          defaultValue=""
                          onChange={e => { if (e.target.value) onAssignToSquad(team.id, e.target.value); }}
                          style={styles.assignSelect}
                        >
                          <option value="" disabled>— pick a squad —</option>
                          {squads.map(s => (
                            <option key={s.id} value={s.id}>{s.name}</option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Squads list */}
        {squads.length === 0 && !showSquadInput && unassignedTeams.length === 0 && (
          <p style={styles.emptyMsg}>No teams or squads yet — use the buttons above to get started.</p>
        )}

        {squads.map(squad => (
          <SquadSection
            key={squad.id}
            squad={squad}
            activeTeamId={activeTeamId}
            onSetActiveTeam={onSetActiveTeam}
            onRemoveMember={onRemoveMember}
            onClearTeam={onClearTeam}
            onRenameTeam={onRenameTeam}
            onDeleteTeam={onDeleteTeam}
            onAddTeamToSquad={onAddTeamToSquad}
            onDeleteSquad={onDeleteSquad}
            collapsed={collapsedSquads.has(squad.id)}
            onToggleSquad={toggleSquad}
            collapsedTeams={collapsedTeams}
            onToggleTeam={toggleTeam}
            onExpandAllTeams={expandAllTeams}
            onCollapseAllTeams={collapseAllTeams}
          />
        ))}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main Page
// ─────────────────────────────────────────────────────────────────────────────
export default function Fearlesslocke() {
  const [pokemonList, setPokemonList] = useState([]);
  const [types, setTypes] = useState([]);
  const [filterText, setFilterText] = useState('');
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDex, setSelectedDex] = useState('1');

  const [greyedPokemon, setGreyedPokemon] = useState(() => {
    try { return JSON.parse(localStorage.getItem('greyedPokemon')) || []; } catch { return []; }
  });
  const [monolocke, setMonolocke] = useState(false);

  // squads: [{ id, type, teams: [{ id, name, members: [{id, name, sprite}] }] }]
  const [squads, setSquads] = useState(() => {
    try { return JSON.parse(localStorage.getItem('fearlessSquads')) || []; } catch { return []; }
  });
  const [unassignedTeams, setUnassignedTeams] = useState(() => {
    try { return JSON.parse(localStorage.getItem('fearlessUnassigned')) || []; } catch { return []; }
  });
  const [squadsOpen, setSquadsOpen] = useState(false);
  const [triggerSquadInput, setTriggerSquadInput] = useState(0);
  const [newSquadId, setNewSquadId] = useState(null);
  const [activeTeamId, setActiveTeamId] = useState(null);

  useEffect(() => {
    try { localStorage.setItem('greyedPokemon', JSON.stringify(greyedPokemon)); } catch {}
  }, [greyedPokemon]);

  useEffect(() => {
    try { localStorage.setItem('fearlessSquads', JSON.stringify(squads)); } catch {}
  }, [squads]);

  useEffect(() => {
    try { localStorage.setItem('fearlessUnassigned', JSON.stringify(unassignedTeams)); } catch {}
  }, [unassignedTeams]);

  useEffect(() => {
    setLoading(true); setError(null);
    Promise.all([fetchPokedex(selectedDex), fetchAllTypes()])
      .then(([pokes, typeData]) => { setPokemonList(pokes); setTypes(typeData); })
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, [selectedDex]);

  useEffect(() => {
    if (monolocke) setSelectedTypes(prev => prev.length > 1 ? [prev[0]] : prev);
  }, [monolocke]);

  // ── Helpers: walk all teams across all squads ────────────────────────────────
  const allSquadTeams = squads.flatMap(s => s.teams);
  const allTeams = [...allSquadTeams, ...unassignedTeams];
  const activeTeam = allTeams.find(t => t.id === activeTeamId) || null;

  // ── Squads CRUD ──────────────────────────────────────────────────────────────
  const createSquad = name => {
    const id = Date.now().toString();
    setSquads(prev => [...prev, { id, name, teams: [] }]);
    setNewSquadId(id);
    setSquadsOpen(true);
  };

  // ── Teams CRUD (unassigned) ───────────────────────────────────────────────
  const createTeam = () => {
    const id = Date.now().toString();
    const name = `Team ${allTeams.length + 1}`;
    setUnassignedTeams(prev => [...prev, { id, name, members: [] }]);
    setActiveTeamId(id);
    setSquadsOpen(true);
  };

  const assignToSquad = (teamId, squadId) => {
    const team = unassignedTeams.find(t => t.id === teamId);
    if (!team) return;
    setUnassignedTeams(prev => prev.filter(t => t.id !== teamId));
    setSquads(prev => prev.map(s =>
      s.id === squadId ? { ...s, teams: [...s.teams, team] } : s
    ));
  };

  const deleteSquad = setId => {
    const squad = squads.find(s => s.id === setId);
    if (squad?.teams.some(t => t.id === activeTeamId)) {
      setActiveTeamId(null);
    }
    setSquads(prev => prev.filter(s => s.id !== setId));
  };

  const addTeamToSquad = setId => {
    const id = Date.now().toString();
    setSquads(prev => prev.map(s => {
      if (s.id !== setId) return s;
      return { ...s, teams: [...s.teams, { id, name: `Team ${s.teams.length + 1}`, members: [] }] };
    }));
    setActiveTeamId(id);
    setNewSquadId(setId); // ensures parent squad expands even before state settles
  };

  const deleteTeam = teamId => {
    setSquads(prev => prev.map(s => ({ ...s, teams: s.teams.filter(t => t.id !== teamId) })));
    setUnassignedTeams(prev => prev.filter(t => t.id !== teamId));
    if (activeTeamId === teamId) setActiveTeamId(null);
  };

  const renameTeam = (teamId, name) => {
    setSquads(prev => prev.map(s => ({
      ...s, teams: s.teams.map(t => t.id === teamId ? { ...t, name } : t)
    })));
    setUnassignedTeams(prev => prev.map(t => t.id === teamId ? { ...t, name } : t));
  };

  const clearTeam = async teamId => {
    const team = allTeams.find(t => t.id === teamId);
    if (team && team.members.length > 0) {
      // Fetch chain IDs for every member and collect all IDs to un-grey
      const chainIdSets = await Promise.all(
        team.members.map(m => fetchChainIds(m.id, m.name))
      );
      const allChainIds = new Set(chainIdSets.flat());
      setGreyedPokemon(prev => prev.filter(id => !allChainIds.has(id)));
    }
    setSquads(prev => prev.map(s => ({
      ...s, teams: s.teams.map(t => t.id === teamId ? { ...t, members: [] } : t)
    })));
    setUnassignedTeams(prev => prev.map(t => t.id === teamId ? { ...t, members: [] } : t));
  };

  function collectSpeciesNames(chain) {
    let names = [chain.species.name];
    chain.evolves_to.forEach(child => { names = names.concat(collectSpeciesNames(child)); });
    return names;
  }

  const fetchChainIds = async (pokemonId, pokemonName) => {
    let speciesNames = [];
    try {
      const sp = await fetch(`${API_BASE}pokemon-species/${pokemonName}`);
      if (!sp.ok) throw new Error();
      const spData = await sp.json();
      const ch = await fetch(spData.evolution_chain.url);
      if (!ch.ok) throw new Error();
      speciesNames = collectSpeciesNames((await ch.json()).chain);
    } catch {}
    return pokemonList.filter(p => speciesNames.includes(p.name)).map(p => p.id);
  };

  const removeMember = async (teamId, pokemonId) => {
    setSquads(prev => prev.map(s => ({
      ...s, teams: s.teams.map(t =>
        t.id === teamId ? { ...t, members: t.members.filter(p => p.id !== pokemonId) } : t
      )
    })));
    setUnassignedTeams(prev => prev.map(t =>
      t.id === teamId ? { ...t, members: t.members.filter(p => p.id !== pokemonId) } : t
    ));
    const poke = pokemonList.find(p => p.id === pokemonId);
    if (!poke) return;
    const chainIds = await fetchChainIds(pokemonId, poke.name);
    setGreyedPokemon(prev => prev.filter(id => !chainIds.includes(id)));
  };

  const handleAddToTeam = async (pokemonId, pokemonName) => {
    if (!activeTeamId) return;
    const poke = pokemonList.find(p => p.id === pokemonId);
    if (!poke) return;

    const newMember = { id: poke.id, name: poke.name, sprite: poke.sprite || poke.spriteUrl || poke.image };

    setSquads(prev => prev.map(s => ({
      ...s, teams: s.teams.map(t => {
        if (t.id !== activeTeamId) return t;
        if (t.members.some(m => m.id === pokemonId)) return t;
        return { ...t, members: [...t.members, newMember] };
      })
    })));
    setUnassignedTeams(prev => prev.map(t => {
      if (t.id !== activeTeamId) return t;
      if (t.members.some(m => m.id === pokemonId)) return t;
      return { ...t, members: [...t.members, newMember] };
    }));

    const chainIds = await fetchChainIds(pokemonId, pokemonName);
    setGreyedPokemon(prev => Array.from(new Set([...prev, ...chainIds])));
  };

  const exportData = () => {
    const payload = { squads, unassignedTeams, exportedAt: new Date().toISOString() };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `fearlesslocke-teams-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const importData = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = e => {
      try {
        const data = JSON.parse(e.target.result);
        if (data.squads) setSquads(prev => mergeById(prev, data.squads));
        if (data.unassignedTeams) setUnassignedTeams(prev => mergeById(prev, data.unassignedTeams));
      } catch {
        alert('Invalid file — could not import teams.');
      }
    };
    reader.readAsText(file);
  };

  // Merge imported items, skipping any IDs already present
  function mergeById(existing, incoming) {
    const existingIds = new Set(existing.map(x => x.id));
    const newItems = incoming.filter(x => !existingIds.has(x.id));
    return [...existing, ...newItems];
  }

  const handleTypeClick = typeName => {
    setSelectedTypes(prev => {
      if (prev.includes(typeName)) return prev.filter(t => t !== typeName);
      if (monolocke) return [typeName];
      if (prev.length < 2) return [...prev, typeName];
      return [prev[1], typeName];
    });
  };

  const filteredList = pokemonList.filter(p =>
    p.name.toLowerCase().includes(filterText.toLowerCase()) &&
    selectedTypes.every(t => p.types.includes(t))
  );

  const totalTeams = squads.reduce((n, s) => n + s.teams.length, 0) + unassignedTeams.length;
  const disabledIds = greyedPokemon;

  const activeTypeColour = activeTeam
    ? (TYPE_COLOURS[squads.find(s => s.teams.some(t => t.id === activeTeamId))?.type] || '#e8b84b')
    : '#e8b84b';

  if (loading) return (
    <div style={{ background: '#0e1117', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#c8a84b', fontFamily: "'Georgia', serif", letterSpacing: '0.1em' }}>
      Loading Pokémon & types…
    </div>
  );
  if (error) return (
    <div style={{ background: '#0e1117', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#e08080', fontFamily: "'Georgia', serif" }}>
      Error: {error}
    </div>
  );

  return (
    <div className="App" style={{ background: 'radial-gradient(ellipse at 50% 0%, #1a2340 0%, #0e1117 60%)', minHeight: '100vh', fontFamily: "'Georgia', 'Times New Roman', serif", position: 'relative' }}>
      {/* Grain overlay */}
      <div style={{
        position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 0,
        backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 200 200\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'n\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.75\' numOctaves=\'4\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23n)\' opacity=\'0.04\'/%3E%3C/svg%3E")',
        opacity: 0.5,
      }} />
      <style>{`
        .fl-btn:hover { background: rgba(200,168,75,0.1) !important; border-color: #c8a84b !important; color: #f0d060 !important; }
        .fl-btn-squad:hover { background: rgba(112,56,248,0.1) !important; border-color: #a080f0 !important; color: #c0a0ff !important; }
      `}</style>

      <div style={{ position: 'relative', zIndex: 1 }}>
      <TypeGrid types={types} selectedTypes={selectedTypes} onTypeClick={handleTypeClick} />

      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexWrap: 'wrap', gap: '0.75rem',
        padding: '0.85rem 1.5rem',
      }}>
        <DexSelector
          options={[
            { id: '1',  name: 'National' }, { id: '2',  name: 'Kanto'  },
            { id: '7',  name: 'Johto'    }, { id: '15', name: 'Hoenn'  },
            { id: '6',  name: 'Sinnoh'   }, { id: '9',  name: 'Unova'  },
            { id: '12', name: 'Kalos'    }, { id: '21', name: 'Alola'  },
            { id: '27', name: 'Galar'    }, { id: '31', name: 'Paldea' },
          ]}
          value={selectedDex}
          onChange={setSelectedDex}
        />

        <label style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', color: '#c8a84b', fontSize: '0.88rem', fontWeight: 600, letterSpacing: '0.04em' }}>
          <input type="checkbox" checked={monolocke} onChange={() => setMonolocke(!monolocke)} style={{ marginRight: '0.5rem', accentColor: '#c8a84b' }} />
          Toggle Monolocke
        </label>

        <button
          className="fl-btn"
          onClick={() => { setSquadsOpen(true); }}
          style={{ ...styles.createTeamBtn, ...(squadsOpen ? styles.createTeamBtnActive : {}) }}
        >
          📋 My Teams
          {totalTeams > 0 && <span style={styles.teamBadge}>{totalTeams}</span>}
        </button>
        <button
          className="fl-btn"
          onClick={createTeam}
          style={styles.createTeamBtn}
        >
          + New Team
        </button>
        <button
          className="fl-btn-squad"
          onClick={() => { setSquadsOpen(true); setTriggerSquadInput(c => c + 1); }}
          style={styles.createSquadBtn}
        >
          + New Squad
        </button>
      </div>

      <SquadsManager
        squads={squads}
        unassignedTeams={unassignedTeams}
        isOpen={squadsOpen}
        onClose={() => setSquadsOpen(false)}
        triggerSquadInput={triggerSquadInput}
        newSquadId={newSquadId}
        activeTeamId={activeTeamId}
        onSetActiveTeam={setActiveTeamId}
        onCreateSquad={createSquad}
        onDeleteSquad={deleteSquad}
        onAddTeamToSquad={addTeamToSquad}
        onAssignToSquad={assignToSquad}
        onDeleteTeam={deleteTeam}
        onRemoveMember={removeMember}
        onClearTeam={clearTeam}
        onRenameTeam={renameTeam}
        onExport={exportData}
        onImport={importData}
      />

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '1rem 0 0.5rem', padding: '0 1rem' }}>
        <SearchBar
          value={filterText}
          onChange={setFilterText}
          onResetFilters={() => { setFilterText(''); setSelectedTypes([]); }}
          onResetGreyed={() => {
            setGreyedPokemon([]);
            setSquads([]);
            setUnassignedTeams([]);
            setActiveTeamId(null);
          }}
        />
      </div>

      {activeTeam && (
        <div style={{ ...styles.teamModeBar, borderColor: activeTypeColour, color: activeTypeColour }}>
          ✦ Adding to <strong>{activeTeam.name}</strong> — hit <strong>+ Add</strong> on any card
        </div>
      )}

      <PokemonGrid
        pokemonList={filteredList}
        greyedPokemon={greyedPokemon}
        onCardClick={() => {}}
        activeTeamId={activeTeamId}
        teamMemberIds={disabledIds}
        onAddToTeam={(id, name) => handleAddToTeam(id, name)}
        onRemoveFromTeam={(id, name) => removeMember(activeTeamId, id, name)}
      />
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Styles
// ─────────────────────────────────────────────────────────────────────────────
const styles = {
  createTeamBtn: {
    padding: '0.45rem 1.1rem',
    borderRadius: '6px',
    border: '1px solid rgba(200,168,75,0.45)',
    background: 'transparent',
    color: '#c8a84b',
    fontWeight: 700,
    fontSize: '0.82rem',
    cursor: 'pointer',
    position: 'relative',
    transition: 'all 0.2s ease',
    letterSpacing: '0.06em',
    fontFamily: "'Georgia', serif",
    textTransform: 'uppercase',
  },
  createTeamBtnActive: {
    background: 'rgba(200,168,75,0.15)',
    borderColor: '#c8a84b',
    color: '#f0d060',
    boxShadow: '0 0 12px rgba(200,168,75,0.15)',
  },
  teamBadge: {
    position: 'absolute',
    top: '-7px', right: '-7px',
    background: '#8a3030',
    color: '#f0d0d0',
    borderRadius: '50%',
    width: '17px', height: '17px',
    fontSize: '0.65rem', fontWeight: 700,
    display: 'inline-flex',
    alignItems: 'center', justifyContent: 'center',
    border: '1px solid rgba(200,100,100,0.4)',
  },

  // Manager
  managerOverlay: {
    width: '100%', display: 'flex', justifyContent: 'center',
    padding: '0.5rem 1rem', boxSizing: 'border-box',
  },
  managerBox: {
    width: '100%', maxWidth: '900px',
    background: 'rgba(14,17,23,0.85)',
    border: '1px solid rgba(200,168,75,0.25)',
    borderRadius: '14px',
    padding: '1rem 1.25rem',
    boxShadow: '0 8px 40px rgba(0,0,0,0.5), inset 0 1px 0 rgba(200,168,75,0.08)',
    backdropFilter: 'blur(8px)',
  },
  managerTopBar: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    marginBottom: '0.85rem',
    paddingBottom: '0.75rem',
    borderBottom: '1px solid rgba(200,168,75,0.12)',
  },
  managerTitle: {
    fontWeight: 800, fontSize: '1.05rem', color: '#c8a84b', letterSpacing: '0.08em',
    fontFamily: "'Georgia', serif",
  },
  newSquadBtn: {
    padding: '0.3rem 0.9rem',
    borderRadius: '6px',
    border: '1px dashed rgba(200,168,75,0.5)',
    background: 'transparent',
    color: '#c8a84b',
    fontSize: '0.82rem', fontWeight: 700, cursor: 'pointer',
    fontFamily: "'Georgia', serif",
  },

  // Squad input
  squadInput: {
    background: 'rgba(200,168,75,0.04)',
    border: '1px solid rgba(200,168,75,0.15)',
    borderRadius: '10px',
    padding: '0.75rem 1rem',
    marginBottom: '0.85rem',
  },
  squadInputLabel: {
    fontSize: '0.75rem', color: '#806050', marginBottom: '0.5rem', textAlign: 'center',
    letterSpacing: '0.08em', textTransform: 'uppercase',
  },
  squadNameInput: {
    background: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(200,168,75,0.4)',
    borderRadius: '6px',
    color: '#e8e0d0',
    fontWeight: 600,
    fontSize: '0.9rem',
    padding: '0.35rem 0.75rem',
    outline: 'none',
    width: '260px',
    fontFamily: "'Georgia', serif",
  },

  // Squad section
  squadSection: {
    border: '1px solid',
    borderRadius: '10px',
    marginBottom: '0.6rem',
    overflow: 'hidden',
  },
  squadHeader: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '0.6rem 0.9rem',
    background: 'rgba(200,168,75,0.04)',
    cursor: 'pointer',
    userSelect: 'none',
  },
  typeChip: {
    padding: '0.15rem 0.6rem',
    borderRadius: '4px',
    fontSize: '0.72rem', fontWeight: 800,
    color: '#111',
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
  },
  addTeamBtn: {
    padding: '0.2rem 0.65rem',
    borderRadius: '5px',
    border: '1px solid',
    background: 'transparent',
    fontSize: '0.75rem', fontWeight: 700, cursor: 'pointer',
  },
  collapseIcon: {
    fontSize: '0.7rem', marginLeft: '0.25rem',
  },
  teamsContainer: {
    padding: '0.5rem 0.75rem 0.75rem',
    display: 'flex', flexDirection: 'column', gap: '0.5rem',
  },

  // Team panel
  teamPanel: {
    border: '1px solid',
    borderRadius: '8px',
    padding: '0.5rem 0.75rem',
    background: 'rgba(255,255,255,0.02)',
    transition: 'border-color 0.15s',
  },
  teamPanelHeader: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    marginBottom: '0.4rem',
  },
  teamPanelName: {
    fontWeight: 700, fontSize: '0.9rem', cursor: 'pointer',
  },
  editHint: {
    fontSize: '0.65rem', opacity: 0.4, marginLeft: '3px',
  },
  nameInput: {
    background: 'rgba(255,255,255,0.07)',
    border: '1px solid #e8b84b',
    borderRadius: '5px',
    color: '#e8b84b', fontWeight: 700, fontSize: '0.9rem',
    padding: '0.1rem 0.4rem', outline: 'none',
  },
  countBadge: { fontSize: '0.68rem', color: '#504840' },

  selectBtn: {
    padding: '0.2rem 0.65rem',
    borderRadius: '5px',
    border: '1px solid',
    fontSize: '0.75rem', fontWeight: 700, cursor: 'pointer',
    transition: 'all 0.15s',
    fontFamily: "'Georgia', serif",
  },
  createSquadBtn: {
    padding: '0.45rem 1.1rem',
    borderRadius: '6px',
    border: '1px solid rgba(112,56,248,0.45)',
    background: 'transparent',
    color: '#a080f0',
    fontWeight: 700,
    fontSize: '0.82rem',
    cursor: 'pointer',
    position: 'relative',
    transition: 'all 0.2s ease',
    letterSpacing: '0.06em',
    fontFamily: "'Georgia', serif",
    textTransform: 'uppercase',
  },
  expandCollapseBtn: {
    background: 'none',
    border: '1px solid rgba(200,168,75,0.2)',
    color: '#806050',
    borderRadius: '5px',
    padding: '0.2rem 0.55rem',
    fontSize: '0.72rem',
    cursor: 'pointer',
    fontWeight: 600,
    fontFamily: "'Georgia', serif",
  },
  clearBtn: {
    background: 'none', border: '1px solid rgba(224,82,82,0.4)', color: '#c06060',
    borderRadius: '5px', padding: '0.2rem 0.55rem',
    fontSize: '0.72rem', cursor: 'pointer', fontWeight: 600,
  },
  deleteBtn: {
    background: 'none', border: 'none', cursor: 'pointer',
    fontSize: '0.85rem', opacity: 0.4, padding: '0 2px',
  },
  closeBtn: {
    background: 'none', border: 'none',
    color: '#604840', fontSize: '1rem',
    cursor: 'pointer', fontWeight: 700,
  },
  assignSelect: {
    background: 'rgba(14,17,23,0.8)',
    border: '1px solid rgba(200,168,75,0.2)',
    borderRadius: '5px',
    color: '#c8a84b',
    fontSize: '0.75rem',
    padding: '0.15rem 0.4rem',
    cursor: 'pointer',
    outline: 'none',
    fontFamily: "'Georgia', serif",
  },
  squadMeta: {
    fontSize: '0.72rem', color: '#504840',
  },
  emptyMsg: {
    textAlign: 'center', color: '#404040', fontSize: '0.82rem', padding: '0.5rem 0',
    fontStyle: 'italic',
  },
  slotsGrid: {
    display: 'flex', flexWrap: 'wrap', gap: '0.4rem', marginTop: '0.4rem',
  },
  filledSlot: {
    border: '2px solid rgba(232,184,75,0.5)',
    borderRadius: '8px', width: '72px',
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    position: 'relative', background: 'rgba(232,184,75,0.06)',
    padding: '0.2rem 0.1rem',
  },
  slotSprite: { width: '48px', height: '48px', imageRendering: 'pixelated' },
  slotName: {
    fontSize: '0.55rem', color: '#ddd',
    textTransform: 'capitalize', textAlign: 'center',
  },
  removeBtn: {
    position: 'absolute', top: '1px', right: '3px',
    background: 'none', border: 'none',
    color: '#e05252', fontWeight: 700, fontSize: '0.8rem',
    cursor: 'pointer', padding: 0, lineHeight: 1,
  },

  teamModeBar: {
    textAlign: 'center', fontSize: '0.82rem',
    background: 'rgba(200,168,75,0.06)',
    border: '1px solid rgba(200,168,75,0.2)',
    borderRadius: '6px',
    padding: '0.5rem', marginBottom: '0.5rem',
    letterSpacing: '0.04em',
    maxWidth: '600px', margin: '0 auto 0.5rem',
    fontFamily: "'Georgia', serif",
  },
};